import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Box, BoxProps, Flex, Text } from '@chakra-ui/react';
import { CbAvatar } from '../CbAvatar';
import Pencil from '../Icons/Pencil';
import GiftOne from '../Icons/GiftOne';
import Party from '../Icons/Party/Party';
import { formatBirthday, formatAnniversary } from '../../helpers/dates';
import styles from './CbGeneralInfoCard.module.css';
interface DecoratedSubTextProps extends BoxProps {
  section: string;
  text: string;
  icon: ReactNode;
}
const DecoratedSubText: FC<DecoratedSubTextProps> = ({
  text,
  icon,
  section,
  ...props
}) => {
  return <Flex marginRight={4} alignItems="center" {...props} data-sentry-element="Flex" data-sentry-component="DecoratedSubText" data-sentry-source-file="CbGeneralInfoCard.tsx">
      {icon}
      <Text fontSize="sm" marginX="8px" color="text.secondary" fontWeight="bold" data-sentry-element="Text" data-sentry-source-file="CbGeneralInfoCard.tsx">
        {`${section}:`}
      </Text>
      <Text fontSize="sm" color="text.secondary" data-sentry-element="Text" data-sentry-source-file="CbGeneralInfoCard.tsx">
        {text}
      </Text>
    </Flex>;
};
export interface CbGeneralInfoCardProps extends BoxProps {
  name: string;
  jobTitle?: string;
  location?: string;
  birthday?: string;
  anniversary?: string;
  avatarUrl?: string;
  badgeUrl: string;
  description?: string;
  isLoading?: boolean;
  onEditAvatar?: () => void;
  hasAvatarPreference?: boolean;
}
export const CbGeneralInfoCard: FC<CbGeneralInfoCardProps> = ({
  name,
  jobTitle,
  location,
  birthday,
  anniversary,
  avatarUrl,
  badgeUrl,
  description,
  isLoading,
  onEditAvatar,
  hasAvatarPreference,
  ...props
}) => {
  const hasSeparator = !!jobTitle && !!location;
  const prevPrefRef = useRef(hasAvatarPreference);
  const [isFlipping, setIsFlipping] = useState(false);
  useEffect(() => {
    if (hasAvatarPreference && !prevPrefRef.current) {
      setIsFlipping(true);
      const timer = setTimeout(() => setIsFlipping(false), 500);
      return () => clearTimeout(timer);
    }
    prevPrefRef.current = hasAvatarPreference;
  }, [hasAvatarPreference]);
  const badgeClass = !hasAvatarPreference ? styles['edit-badge-nudge'] : isFlipping ? styles['edit-badge-flip'] : '';
  return <Flex {...props} data-sentry-element="Flex" data-sentry-component="CbGeneralInfoCard" data-sentry-source-file="CbGeneralInfoCard.tsx">
      <Box position="relative" cursor={onEditAvatar ? 'pointer' : undefined} onClick={onEditAvatar} role={onEditAvatar ? 'button' : undefined} aria-label={onEditAvatar ? 'Change profile picture' : undefined} _hover={onEditAvatar ? {
      '& > .edit-badge': {
        opacity: 1,
        transform: 'scale(1)'
      }
    } : undefined} data-sentry-element="Box" data-sentry-source-file="CbGeneralInfoCard.tsx">
        <CbAvatar name={name} avatarSize="2xl" image={avatarUrl} badgeUrl={badgeUrl} borderWidth="0px" borderRadius={100} data-sentry-element="CbAvatar" data-sentry-source-file="CbGeneralInfoCard.tsx" />
        {onEditAvatar && <Flex className={`edit-badge ${badgeClass}`} position="absolute" bottom="2px" left="2px" boxSize="34px" borderRadius="full" bg="white" borderWidth="2px" borderColor="cloud.200" align="center" justify="center" opacity={1} transition="all 0.2s ease" shadow="sm">
            <Pencil boxSize="18px" color="text.secondary" />
          </Flex>}
      </Box>
      <Box marginLeft={6} data-sentry-element="Box" data-sentry-source-file="CbGeneralInfoCard.tsx">
        <Text fontSize="20px" fontFamily="codenext" fontWeight="bold" color="text.primaryDark" noOfLines={1} data-sentry-element="Text" data-sentry-source-file="CbGeneralInfoCard.tsx">
          {name}
        </Text>
        <Text fontSize="sm" color="text.secondary" noOfLines={1} data-sentry-element="Text" data-sentry-source-file="CbGeneralInfoCard.tsx">
          {jobTitle && <Box as="span">{jobTitle}</Box>}
          {hasSeparator && <Box as="span" marginX={1}>
              {'•'}
            </Box>}
          {location && <Box as="span">{location}</Box>}
        </Text>
        <Box marginTop={3} data-sentry-element="Box" data-sentry-source-file="CbGeneralInfoCard.tsx">
          {birthday && <DecoratedSubText section="Birthday" text={formatBirthday(new Date(birthday))} icon={<GiftOne color="chonky.500" />} marginBottom={2} />}
          {anniversary && <DecoratedSubText section="Anniversary" text={formatAnniversary(new Date(anniversary))} icon={<Party color="chonky.500" />} />}
        </Box>
        <Text marginTop={4} noOfLines={2} color="text.secondary" fontSize="sm" data-sentry-element="Text" data-sentry-source-file="CbGeneralInfoCard.tsx">
          {description}
        </Text>
      </Box>
    </Flex>;
};